import { Component, ElementRef, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { alpha2ToAlpha3 } from 'i18n-iso-countries';
import { Subscription } from 'rxjs';

import { UserService } from '~/auth/services/user/user.service';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { PostalAddress } from '~/checkout/model/postal-address.model';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { getCountryFromLocale } from '~/core/helpers/locale/locale.helper';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { ValidatorsService } from '~/core/services/validators.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';

@Component({
  selector: 'app-billing-address',
  templateUrl: './billing-address.component.html',
  styleUrls: ['./billing-address.component.scss'],
})
export class BillingAddressComponent implements OnInit {
  private readonly subscriptions = new Subscription();

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  public address: PostalAddress;
  public addressForm: FormGroup;
  public isMobileDevice: boolean;

  constructor(
    private readonly userService: UserService,
    private readonly formBuilder: FormBuilder,
    private readonly redirectionService: RedirectionService,
    public readonly appBillingAddressElementRef: ElementRef,
    public readonly localizeRouter: LocalizeRouterService,
    private readonly titleService: TitleService,
    private readonly validatorsService: ValidatorsService,
    private readonly translateService: TranslateService,
    private readonly toggleService: ToggleService,
    private readonly matchMediaService: MatchMediaService
  ) {}

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.billing_address.title');

    this.addressForm = this.formBuilder.nonNullable.group({
      streetAddress: ['', [Validators.required, noHtmlTagValidator()]],
      postalCode: [
        '',
        [Validators.required, ...this.validatorsService.postalCode()],
      ],
      addressLocality: ['', [Validators.required, noHtmlTagValidator()]],
      addressCountry: [
        getCountryFromLocale(
          this.translateService.currentLang
        )?.toLocaleUpperCase(),
        Validators.required,
      ],
    });

    this.subscriptions.add(
      this.userService.getAddress().subscribe({
        next: (address) => {
          this.address = address;
        },
        error: (error) => {
          throw new Error(error);
        },
        complete: () => {
          if (this.address && this.addressForm) {
            if (this.addressForm.controls.streetAddress) {
              this.addressForm.controls.streetAddress.setValue(
                this.address.streetAddress
              );
            }
            if (this.addressForm.controls.postalCode) {
              this.addressForm.controls.postalCode.setValue(
                this.address.postalCode
              );
            }
            if (this.addressForm.controls.addressLocality) {
              this.addressForm.controls.addressLocality.setValue(
                this.address.addressLocality
              );
            }
            if (this.addressForm.controls.addressCountry) {
              this.addressForm.controls.addressCountry.setValue(
                this.address.addressCountryCode
              );
            }
            if (this.addressForm.controls.legalTerms) {
              this.addressForm.controls.legalTerms.setValue(false);
            }
          }
        },
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  public get backLink(): BackLinkInterface {
    const redirection = '/attendee.homepage';
    return {
      path: this.localizeRouter.translateRoute(redirection).toString(),
    };
  }

  get formControls(): {
    [key: string]: AbstractControl;
  } {
    return this.addressForm.controls;
  }

  public errors(formControlName: string): ValidationErrors | null {
    const control = this.addressForm.get(formControlName);
    return control?.touched ? control?.errors : null;
  }

  /** check if data values has changed */
  public hasChanged(): boolean {
    return (
      this.formControls.streetAddress.value !== this.address.streetAddress ||
      this.formControls.postalCode.value !== this.address.postalCode ||
      this.formControls.addressLocality.value !==
        this.address.addressLocality ||
      this.formControls.addressCountry.value !== this.address.addressCountry
    );
  }

  /** valid form */
  public onSubmit(): Subscription | Promise<boolean> {
    // stop here if form is invalid
    if (this.addressForm.invalid) {
      return;
    }

    if (this.address && !this.hasChanged()) {
      return this.redirectionService.redirect('/checkout.payment');
    }

    // retrieving phone number and the rest from form values
    const address = { ...this.addressForm.value };

    address.addressCountry = `/countries/${alpha2ToAlpha3(
      address.addressCountry
    )}`;

    // update address & user
    return this.userService.updateAddress(address).subscribe(() => {
      return this.redirectionService.redirect('/checkout.payment');
    });
  }
}
